/**
 * API service for chat module settings
 */
import { apiGet, apiPut } from '../../../shared/api/axiosInstance';

const USER_SETTINGS_URL = '/api/v1/ai-chat/settings/user';
const MODEL_CATEGORIES_URL = '/api/v1/ai-chat/settings/user/model-categories';
const TENANT_SETTINGS_URL = '/api/v1/ai-chat/settings/tenant';
const SYSADMIN_SETTINGS_URL = '/api/v1/ai-chat/settings/sysadmin';

// ========== Types ==========

export interface ModelCategory {
  id: string;
  name: string;
  description: string;
  tier: string;
  is_default: boolean;
  selected_model: string | null;
}

export interface ModelCategoriesResponse {
  categories: ModelCategory[];
  provider: {
    id: string;
    name: string;
  } | null;
}

// The model category is chosen per chat (chats.model_category), not per user —
// this response only reports the resolved effective one.
export interface ChatUserSettings {
  effective_model_category: string;
  allow_user_model_choice: boolean;
}

export interface ChatTenantSettings {
  id?: string;
  tenant_id?: string;
  allow_user_model_choice: boolean;
  default_model_category: string | null;
  system_prompt: string | null;
  inherited_system_prompt: string | null;
  settings?: Record<string, unknown>;
  updated_by?: string;
}

export interface ChatTenantSettingsUpdate {
  allow_user_model_choice?: boolean;
  default_model_category?: string | null;
  system_prompt?: string | null;
}

export interface ChatSysadminSettings {
  id?: string;
  default_model_category: string;
  system_prompt: string | null;
  settings?: Record<string, unknown>;
  updated_by?: string;
}

export interface ChatSysadminSettingsUpdate {
  default_model_category?: string;
  system_prompt?: string | null;
}

// ========== API ==========

export const chatSettingsApi = {
  // User settings
  getUserSettings: async (): Promise<ChatUserSettings> => {
    return apiGet<ChatUserSettings>(USER_SETTINGS_URL);
  },

  getModelCategories: async (): Promise<ModelCategoriesResponse> => {
    const response = await apiGet<{ data: ModelCategoriesResponse }>(MODEL_CATEGORIES_URL);
    return response.data;
  },

  // Tenant settings (admin)
  getTenantSettings: async (): Promise<ChatTenantSettings> => {
    return apiGet<ChatTenantSettings>(TENANT_SETTINGS_URL);
  },

  updateTenantSettings: async (data: ChatTenantSettingsUpdate): Promise<ChatTenantSettings> => {
    return apiPut<ChatTenantSettings>(TENANT_SETTINGS_URL, data);
  },

  // Sysadmin settings
  getSysadminSettings: async (): Promise<ChatSysadminSettings> => {
    return apiGet<ChatSysadminSettings>(SYSADMIN_SETTINGS_URL);
  },

  updateSysadminSettings: async (data: ChatSysadminSettingsUpdate): Promise<ChatSysadminSettings> => {
    return apiPut<ChatSysadminSettings>(SYSADMIN_SETTINGS_URL, data);
  },
};
